import React, { useState } from "react";
import { toast } from "sonner";
import { Eraser, Loader2, RotateCcw } from "lucide-react";
import { reApi, errMsg } from "@/lib/residentApi";
import { useHasPerm } from "@/lib/placement";

const chip = "inline-flex items-center gap-1 px-2 py-1 border rounded-sm font-mono text-[9px] uppercase tracking-[0.14em]";

function History({ rows, onRestore, busy, testid }) {
  if (!rows.length) return null;
  return (
    <ul className="pt-2 border-t border-white/10 space-y-1" data-testid={`${testid}-history`}>
      {rows.map((c) => (
        <li key={c.id} className="flex items-start gap-2 text-[11px] text-nex-muted" data-testid={`${testid}-history-${c.id}`}>
          <span className="flex-1 min-w-0 break-words">{c.at.slice(0, 10)} · {c.by?.name} · {c.override ? `alert cleared until ${c.expires_at.slice(0, 10)}` : `${c.records.length} record(s) voided`} · “{c.reason}”{c.restored_at && " · restored"}</span>
          {!c.restored_at && <button type="button" disabled={busy} onClick={() => onRestore(c.id)} data-testid={`${testid}-restore-${c.id}`}
            className="inline-flex items-center gap-1 text-sky-300 hover:text-sky-200 shrink-0"><RotateCcw className="h-3 w-3" /> Restore</button>}
        </li>))}
    </ul>
  );
}

/** Super admin only: void the records behind an alert as a mistake (kept in history, restorable), or clear the alert for 7 days. */
export function ClearMistake({ residentId, alertKey, onCleared, testid = `clear-mistake-${alertKey}` }) {
  const sa = useHasPerm("clear_mistakes");
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!sa) return null;
  const url = `/mistakes/${residentId}/${alertKey}`;
  const load = () => reApi.get(url).then((d) => { setData(d); setSel(d.records.map((r) => r.id)); }).catch((e) => toast.error(errMsg(e)));
  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); setReason(""); await load(); onCleared?.(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const submit = () => act(() => reApi.post(url, { ids: sel, reason }), sel.length ? `Voided ${sel.length} record(s) as a mistake` : "Alert cleared as a mistake");
  const restore = (id) => act(() => reApi.post(`/mistakes/clears/${id}/restore`), "Restored");
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  if (!data) return (
    <button type="button" onClick={load} data-testid={`${testid}-open`} className={`${chip} border-rose-400/50 text-rose-200 hover:bg-rose-400/10`}><Eraser className="h-3 w-3" /> Clear as mistake</button>);
  return (
    <div className="w-full p-3 rounded-sm border border-rose-400/40 bg-rose-400/5 space-y-2 text-sm" data-testid={`${testid}-panel`}>
      <div className="text-rose-100 font-medium">Clear as mistake</div>
      {data.records.length ? <>
        <p className="text-xs text-nex-muted">Tick the wrong records. They are voided ($0 owed, out of every balance) but kept in history and can be restored.</p>
        <ul className="space-y-1">{data.records.map((r) => (
          <li key={r.id}><label className="flex items-start gap-2 text-xs text-nex-silver cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={sel.includes(r.id)} onChange={() => toggle(r.id)} data-testid={`${testid}-rec-${r.id}`} />{r.title}</label></li>))}</ul>
        {!sel.length && <p className="text-[11px] text-amber-200">Nothing ticked — the alert itself will be cleared for {data.override_days} days instead.</p>}
      </> : <p className="text-xs text-nex-muted">No single record causes this alert, so it will be marked clear for {data.override_days} days, then checked again.</p>}
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this a mistake? (e.g. resident moved in after this date)" data-testid={`${testid}-reason`}
        className="block w-full box-border px-2 py-1.5 bg-black/40 border border-white/15 rounded-sm text-sm focus:outline-none focus:border-rose-300/60" />
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={busy || reason.trim().length < 5} onClick={submit} data-testid={`${testid}-submit`}
          className={`${chip} border-rose-400/60 text-rose-100 hover:bg-rose-400/15 disabled:opacity-40`}>{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eraser className="h-3 w-3" />} {sel.length ? `Void ${sel.length} record(s)` : "Clear alert"}</button>
        <button type="button" onClick={() => setData(null)} data-testid={`${testid}-cancel`} className={`${chip} border-white/20 text-nex-muted hover:text-nex-silver`}>Close</button>
      </div>
      <History rows={data.history} onRestore={restore} busy={busy} testid={testid} />
    </div>
  );
}

/** Archive Center: every cleared mistake across residents, with Restore. */
export function ClearedMistakesList() {
  const sa = useHasPerm("clear_mistakes");
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const load = () => reApi.get("/mistakes").then((d) => setRows(d.clears)).catch(() => setRows([]));
  React.useEffect(() => { if (sa) load(); }, [sa]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!sa || !rows) return null;
  const restore = async (id) => {
    setBusy(true);
    try { await reApi.post(`/mistakes/clears/${id}/restore`); toast.success("Restored"); await load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const list = showAll ? rows : rows.filter((c) => !c.restored_at);
  return (
    <section className="space-y-2" data-testid="cleared-mistakes">
      <div className="flex items-center gap-3"><h2 className="text-base md:text-lg font-semibold">Cleared mistakes</h2>
        <label className="ml-auto flex items-center gap-1.5 text-xs text-white/60"><input type="checkbox" checked={showAll} onChange={() => setShowAll(!showAll)} data-testid="cleared-mistakes-show-restored" /> Show restored</label></div>
      <p className="text-xs text-white/50">Records a super admin voided as a mistake (or alerts cleared for 7 days). Restore puts them back exactly as they were.</p>
      {!list.length ? <p className="text-sm text-white/50" data-testid="cleared-mistakes-empty">Nothing cleared yet.</p> : (
        <ul className="border border-white/10 rounded-sm divide-y divide-white/5">{list.map((c) => (
          <li key={c.id} className="px-3 py-2 text-sm flex items-start gap-3" data-testid={`cleared-mistake-${c.id}`}>
            <div className="flex-1 min-w-0"><div className="text-white/90">{c.resident_name} · {c.key.replace(/_/g, " ")} · {c.override ? "alert cleared" : `${c.records.length} record(s) voided`}</div>
              <div className="text-xs text-white/50 break-words">{c.at.slice(0, 10)} · {c.by?.name} · “{c.reason}”{c.records.length ? ` · ${c.records.map((r) => r.title).join("; ")}` : ""}</div></div>
            {c.restored_at ? <span className="font-mono text-[10px] text-white/40">Restored</span> : <button type="button" disabled={busy} onClick={() => restore(c.id)} data-testid={`cleared-mistake-restore-${c.id}`}
              className="inline-flex items-center gap-1 text-sky-300 hover:text-sky-200 font-mono text-[10px] uppercase tracking-[0.14em]"><RotateCcw className="h-3 w-3" /> Restore</button>}
          </li>))}</ul>)}
    </section>
  );
}
